import { useContext, useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import { MenuTitles, SettingsMenu } from 'constants/menu';
import { ContentLayout, PageLayout } from '../../layout';
import {
  AgxInfoLabel,
  AgxButton,
  AgxLabel,
  AgxFileUpload,
  useWindowSize,
  Breakpoints,
  Images,
} from '@urbanx/agx-ui-components';
import { AgxSpacer, AgxTextInput, AgxDropdown, useToast } from '@urbanx/agx.ui-components2';
import { PlaceHolderImage } from '../../components';
import AGXColorPicker from '../../components/color-picker/AGXColorPicker';
import { hexToRGB } from '../../utils/colors';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getBranding, UpdateBranding, UploadPublicDocument } from 'services';

import './branding.scss';
import { FormPrompt } from '../../components/FormPrompt';
import { AuthContext } from '@urbanx/agx.auth.client';
import { AgencyBranding } from 'services/functions/agency/settings/getBranding';
import {
  UploadedDocument,
  useInvalidateUseGetAgencyDetails,
  useInvalidateUseGetAllAgencies,
} from '@urbanx/agx.agencies.client';
import { useSelectedAgency } from 'hooks';

interface IBrandingForm {
  backgroundColor: string;
  accentColorOld: string;
  backgroundTextColor: string;
  buttonTextColor: string;
  inboxBackgroundColor: string;
  inboxBackgroundColorOpacity: number;
  sidebarBackgroundColor: string;
  sidebarBackgroundColorOpacity: number;
  logo?: UploadedDocument;

  mainColour: string;
  accentColour: string;
}

export class BrandingForm implements IBrandingForm {
  backgroundColor: string;
  accentColorOld: string;
  backgroundTextColor: string;
  buttonTextColor: string;
  inboxBackgroundColor: string;
  inboxBackgroundColorOpacity: number;
  sidebarBackgroundColor: string;
  sidebarBackgroundColorOpacity: number;
  logo?: UploadedDocument;

  mainColour: string;
  accentColour: string;

  constructor(agencyBranding?: AgencyBranding) {
    const inboxBackgroundColourOpacity = agencyBranding?.inboxBackgroundColorOpacity
      ? parseInt(agencyBranding.inboxBackgroundColorOpacity, 10)
      : 0;

    const sidebarBackgroundColourOpacity = agencyBranding?.sidebarBackgroundColorOpacity
      ? parseInt(agencyBranding.sidebarBackgroundColorOpacity, 10)
      : 0;

    this.backgroundColor = agencyBranding?.backgroundColor ?? '#FFFFFF';
    this.accentColorOld = agencyBranding?.accentColorOld ?? '#000000';
    this.backgroundTextColor = agencyBranding?.backgroundTextColor ?? '#000000';
    this.buttonTextColor = agencyBranding?.buttonTextColor ?? '#000000';
    this.inboxBackgroundColor = agencyBranding?.inboxBackgroundColor ?? '#FFFFFF';
    this.inboxBackgroundColorOpacity = inboxBackgroundColourOpacity;
    this.sidebarBackgroundColor = agencyBranding?.sidebarBackgroundColor ?? '#FFFFFF';
    this.sidebarBackgroundColorOpacity = sidebarBackgroundColourOpacity;
    this.logo = agencyBranding?.logo;

    this.mainColour = agencyBranding?.brandingColours?.mainColour ?? '#FFFFFF';
    this.accentColour = agencyBranding?.brandingColours?.accentColour ?? '#000000';
  }
}

type BrandingKeys = keyof IBrandingForm;

const options = [
  { value: '#FFFFFF', label: 'White' },
  { value: '#000000', label: 'Black' },
];

const BrandingPage = () => {
  const { agencyId } = useParams();
  const queryClient = useQueryClient();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { userAccount, getAuthToken } = useContext(AuthContext);
  const [logo, setLogo] = useState<UploadedDocument | undefined>(undefined);
  const [agencyName, setAgencyName] = useState('');
  const [img, updateImg] = useState('');
  const windowSize = useWindowSize();
  const [tertiaryActiveMenu, setTertiaryActiveMenu] = useState<MenuTitles | undefined>(
    MenuTitles.BRANDING
  );
  const { pushToast } = useToast();
  const [brandingForm, setBrandingForm] = useState(new BrandingForm());
  const { agency: selectedAgency } = useSelectedAgency();

  const invalidateGetAllAgencies = useInvalidateUseGetAllAgencies();
  const invalidateGetAgencyDetails = useInvalidateUseGetAgencyDetails();

  const { data: branding } = useQuery<AgencyBranding | undefined>({
    queryKey: [`branding-${agencyId}`, agencyId],
    queryFn: (queryKey) => getBranding(queryKey, getAuthToken),
  });

  const { mutate: updateBranding } = useMutation({
    mutationFn: UpdateBranding,
    onSuccess: async () => {
      setIsFormDirty(false);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [`branding-${agencyId}`, agencyId],
        }),
        invalidateGetAllAgencies(),
        invalidateGetAgencyDetails(agencyId?.toString() || ''),
      ]);

      pushToast({
        type: 'success',
        message: 'Branding updated successfully',
      });
    },
    onError: (error) => {
      pushToast({
        type: 'error',
        message:
          isAxiosError<string>(error) && error.response?.data
            ? error.response.data
            : 'Error updating branding',
      });
    },
  });
  const { mutate: uploadPublicDocument } = useMutation({
    mutationFn: UploadPublicDocument,
    onSuccess: (r) => {
      updateImg(r?.publicPath || '');
      setLogo(r);
    },
  });

  useEffect(() => {
    if (selectedAgency) {
      setAgencyName(selectedAgency?.name ?? '');
      updateImg(selectedAgency?.agency?.settings?.branding?.logo?.publicPath ?? '');
    }
  }, [selectedAgency]);

  useEffect(() => {
    if (branding) {
      setBrandingForm(new BrandingForm(branding));
      setLogo(branding?.logo);
      setIsFormDirty(false);
    }
  }, [branding]);

  useEffect(() => {
    if (logo?.publicPath !== branding?.logo?.publicPath) setIsFormDirty(true);
    else setIsFormDirty(false);
  }, [logo]);

  const onBrandingChangeHandler = (value: string, prop: BrandingKeys) => {
    if (brandingForm) {
      if (value && brandingForm[prop] !== value) {
        setIsFormDirty(true);
      }

      setBrandingForm({ ...brandingForm, [prop]: value });
    }
  };

  const onSaveChanges = () => {
    updateBranding({
      AgencyId: agencyId || '',
      AgencyName: agencyName,
      MainColour: brandingForm?.mainColour,
      AccentColour: brandingForm?.accentColour,
      BackgroundColor: brandingForm?.backgroundColor,
      BackgroundTextColor: brandingForm?.backgroundTextColor,
      AccentColorOld: brandingForm?.accentColorOld,
      ButtonTextColor: brandingForm?.buttonTextColor,
      InboxBackgroundColor: brandingForm?.inboxBackgroundColor,
      InboxBackgroundColorOpacity: `${brandingForm?.inboxBackgroundColorOpacity}`,
      SidebarBackgroundColor: brandingForm?.sidebarBackgroundColor,
      SidebarBackgroundColorOpacity: `${brandingForm?.sidebarBackgroundColorOpacity}`,
      Logo: logo,
      getAuthToken,
    });
  };

  const uploadLogo = (file: File) => {
    updateImg(URL.createObjectURL(file));
    return new Promise((resolve, reject) => {
      uploadPublicDocument(
        {
          selectedFile: file,
          AgencyId: agencyId,
          getAuthToken,
        },
        {
          onSuccess: resolve,
          onError: reject,
        }
      );
    });
  };

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.SETTINGS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        tertiaryMenu={SettingsMenu}
        secondaryActiveMenu={MenuTitles.SETTINGS}
        tertiaryActiveMenu={tertiaryActiveMenu}
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxSpacer direction="vertical" gap={'sm'}>
          <AgxSpacer
            direction="horizontal"
            fill
            contentJustification="space-between"
            contentAlignment="center"
            className={`borderBottomContainer ${windowSize === Breakpoints.Desktop ? 'pageTitleContainerWithTertiaryMenu' : ''}`}
          >
            {windowSize === Breakpoints.Desktop && <AgxLabel large>{MenuTitles.SETTINGS}</AgxLabel>}
            {windowSize !== Breakpoints.Desktop && (
              <AgxButton
                id="btn-back"
                leftIcon={<Images.ArrowBackOutline />}
                naked
                large
                text={MenuTitles.BRANDING}
                onClick={() => setTertiaryActiveMenu(undefined)}
              />
            )}
            <AgxButton
              text="Save Changes"
              medium
              primary
              disabled={!logo}
              onClick={() => onSaveChanges()}
            />
          </AgxSpacer>
          <AgxSpacer direction="horizontal" fill contentJustification="space-between" gap={'xl'}>
            <AgxSpacer direction="vertical" fill gap={'xs'}>
              <AgxTextInput
                id="agencyName"
                label="Agency Name"
                defaultValue={agencyName}
                onInputValueChange={({ value }: { value: string }) => {
                  if (value && agencyName !== value) {
                    setIsFormDirty(true);
                  }
                  setAgencyName(value);
                }}
                hideOptionalText
                stretch
                type={'text'}
              />

              <AgxSpacer
                direction="horizontal"
                contentAlignment="center"
                className="flexCentre"
                gap={'xs'}
              >
                {!img && <PlaceHolderImage title={agencyName} size={60} />}
                {img && (
                  <img
                    alt={`${agencyName} logo`}
                    style={{
                      maxWidth: '150px',
                      background: brandingForm?.backgroundColor,
                      padding: '20px',
                      borderRadius: '8px',
                    }}
                    src={img}
                  />
                )}
                <AgxFileUpload
                  id="fileUpload"
                  title="Agency logo (required)"
                  buttonText={img ? 'Change' : 'Upload'}
                  isImageFileType
                  uploadFile={(file) => uploadLogo(file)}
                  extensions={['svg']}
                />
              </AgxSpacer>
            </AgxSpacer>
            <AgxSpacer direction="vertical" fill>
              <AgxLabel>Preview</AgxLabel>
              <div
                className="demoLayout__branding"
                style={{ background: brandingForm?.backgroundColor }}
              >
                <div
                  className="demoSubContainer__branding"
                  style={{
                    background: `rgba(${hexToRGB(brandingForm?.inboxBackgroundColor || '')?.r}, ${
                      hexToRGB(brandingForm?.inboxBackgroundColor || '')?.g
                    }, ${hexToRGB(brandingForm?.inboxBackgroundColor || '')?.b}, ${
                      brandingForm?.inboxBackgroundColorOpacity &&
                      brandingForm?.inboxBackgroundColorOpacity / 100
                    })`,
                  }}
                >
                  <AgxLabel>Inbox text</AgxLabel>
                </div>
                <div
                  className="demoSubContainer__branding"
                  style={{
                    background: `rgba(${hexToRGB(brandingForm?.sidebarBackgroundColor || '')?.r}, ${
                      hexToRGB(brandingForm?.sidebarBackgroundColor || '')?.g
                    }, ${hexToRGB(brandingForm?.sidebarBackgroundColor || '')?.b}, ${
                      brandingForm?.sidebarBackgroundColorOpacity &&
                      brandingForm?.sidebarBackgroundColorOpacity / 100
                    })`,
                  }}
                >
                  <span style={{ color: brandingForm?.backgroundTextColor }}>Sidebar text</span>
                </div>
                <div className="horizontalContainer noBorder">
                  <div
                    className="demoSubContainer__branding"
                    style={{
                      background: brandingForm?.accentColorOld,
                    }}
                  >
                    <span style={{ color: brandingForm?.buttonTextColor }}>Button</span>
                  </div>
                  <div className="demoSubContainer__branding">
                    <span style={{ color: '#FFFFFF' }}>Button</span>
                  </div>
                </div>
              </div>
            </AgxSpacer>
          </AgxSpacer>

          <div className="borderBottomContainer">
            <AgxLabel>Brand Colors</AgxLabel>
          </div>

          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            className="borderBottomContainer"
            gap={'sm'}
          >
            <AGXColorPicker
              id="bgColor"
              label="Background"
              defaultColor={branding?.backgroundColor}
              onColorChange={(hexColor) =>
                onBrandingChangeHandler(hexColor.toString(), 'backgroundColor')
              }
            />
            <AGXColorPicker
              id="bgTextColor"
              label="BG Text"
              defaultColor={branding?.backgroundTextColor}
              onColorChange={(v) => onBrandingChangeHandler(v.toString(), 'backgroundTextColor')}
            />
            <AgxInfoLabel id="bglabel" label="The background of the page and the text on it" />
          </AgxSpacer>

          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            className="borderBottomContainer"
            gap={'sm'}
          >
            <AGXColorPicker
              id="accentColor"
              label="Accent"
              defaultColor={branding?.accentColorOld}
              onColorChange={(v) => onBrandingChangeHandler(v.toString(), 'accentColorOld')}
            />
            <AGXColorPicker
              id="buttonText"
              label="Button Text"
              defaultColor={branding?.buttonTextColor}
              onColorChange={(v) => onBrandingChangeHandler(v.toString(), 'buttonTextColor')}
            />

            <AgxInfoLabel id="buttonslabel" label="Used for buttons and action items" />
          </AgxSpacer>

          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            className="borderBottomContainer"
            gap={'sm'}
          >
            <AGXColorPicker
              id="mainV2"
              label="Main"
              defaultColor={branding?.brandingColours?.mainColour ?? branding?.backgroundColor}
              onColorChange={(hexColor) =>
                onBrandingChangeHandler(hexColor.toString(), 'mainColour')
              }
            />
            <AGXColorPicker
              id="accentV2"
              label="Accent V2"
              defaultColor={
                branding?.brandingColours?.accentColour ?? branding?.backgroundTextColor
              }
              onColorChange={(v) => onBrandingChangeHandler(v.toString(), 'accentColour')}
            />
            <AgxInfoLabel id="v2label" label="Main and accent colors for Components V2" />
          </AgxSpacer>

          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            className="borderBottomContainer"
            gap={'sm'}
          >
            <div className="agxSelectContainer__branding">
              <AgxDropdown
                id="inboxBg"
                label="Inbox BG"
                options={options}
                hideOptionalText
                onChange={(value) => onBrandingChangeHandler(value, 'inboxBackgroundColor')}
                selected={brandingForm?.inboxBackgroundColor}
              />
            </div>

            <AgxTextInput
              id="inboxOpacity"
              label="Opacity"
              defaultValue={`${branding?.inboxBackgroundColorOpacity}`}
              onInputValueChange={({ value }: { value: string }) => {
                onBrandingChangeHandler(value, 'inboxBackgroundColorOpacity');
              }}
              hideOptionalText
              type="percent"
            />

            <AgxInfoLabel id="inboxlabel" label="Transparent background behind the inbox pane" />
          </AgxSpacer>

          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            className="borderBottomContainer"
            gap={'sm'}
          >
            <div className="agxSelectContainer__branding">
              <AgxDropdown
                id="sidebarBG"
                label="Sidebar BG"
                options={options}
                hideOptionalText
                onChange={(value) => onBrandingChangeHandler(value, 'sidebarBackgroundColor')}
                selected={brandingForm?.sidebarBackgroundColor}
              />
            </div>
            <AgxTextInput
              id="sidebarOpacity"
              label="Opacity"
              hideOptionalText
              defaultValue={`${branding?.sidebarBackgroundColorOpacity}`}
              onInputValueChange={({ value }: { value: string }) =>
                onBrandingChangeHandler(value, 'sidebarBackgroundColorOpacity')
              }
              type="percent"
            />

            <AgxInfoLabel
              id="inboxlabel"
              label="Transparent background used for the sidebar background"
            />
          </AgxSpacer>
        </AgxSpacer>
      </ContentLayout>
    </PageLayout>
  );
};

export default BrandingPage;
